import { ReactNode, useEffect, useRef } from 'react';

import { isMenuItemAccessible } from 'components/navigation/navigation_wrapper/menu-registry';
import { Navigate, useLocation } from 'react-router-dom';
import { useNotification } from 'app/notifications';
import { useIntl } from 'react-intl';
import { useApp } from 'app/context';

type RequireAccessProps = {
    children: ReactNode;
    itemId: string;
};

type AccessTestOverride = 'allow' | 'deny' | null;

const getAccessTestOverride = (search: string): AccessTestOverride => {

    const value = new URLSearchParams(search).get('accessTest')?.toLowerCase();
    if (value === 'allow' || value === 'deny') return value;

    return null;

};

/**
 * Client-side route permission gate.
 *
 * Renders `children` only when the current user may access the menu item
 * identified by `itemId` — applying the same rules the SideNav uses (role,
 * feature flag, sector and the server-side allowedItems list). When the user
 * lacks access, it surfaces an error toast and redirects to the home page.
 *
 * The backend remains the source of truth for authorization; this guard only
 * prevents the client from accidentally rendering UI the user shouldn't see.
 */
export const RequireAccess = (props: RequireAccessProps) => {

    const { children, itemId } = props;

    const { allowedItems, highestRole, isFeatureEnabled, isSystemMode, selectedPartner } = useApp();
    const { onErrorToast } = useNotification();
    const { formatMessage } = useIntl();
    const { pathname, search } = useLocation();

    const accessTestOverride = getAccessTestOverride(search);

    // A SysAdmin (highest role id 1) is "managing a partner" when they are not in
    // their own system context — i.e. they selected a partner and were routed to
    // the page. Only then do their role/sector come from the managed partner.
    const isSysAdminManagingPartner = highestRole?.id === 1 && !isSystemMode;

    // A non-SysAdmin (Distributor/Reseller) placing an order on behalf of another
    // partner selects that partner (e.g. a Testing Center) via the lookup flow, so
    // the selected partner is no longer their own. Treat that as a managed (proxy)
    // context too, so the whitelisted "must pick a partner" pages stay reachable.
    const isProxyManagingPartner = highestRole?.id !== 1
        && !!selectedPartner
        && !!highestRole
        && selectedPartner.id !== highestRole.partnerId;

    const isManagingProxyPartner = isSysAdminManagingPartner || isProxyManagingPartner;

    // The selected partner is the acting user's own partner (not a proxy/managed
    // partner). This lets a partner reorder their own inventory via the
    // place-order route even when its menu item would otherwise bar them (e.g. a
    // Testing Center reaching "Order More Inventory" from My Inventory).
    const isOwnPartner = !!selectedPartner
        && !!highestRole
        && selectedPartner.id === highestRole.partnerId;

    const computedAccess = accessTestOverride === 'allow' ? true : accessTestOverride === 'deny' ? false : isMenuItemAccessible(itemId, {
        actingRoleId: highestRole?.id ?? 0,
        allowedItems: allowedItems ?? [],
        isFeatureEnabled,
        isManagingProxyPartner,
        isOwnPartner,
        roleId: selectedPartner?.type ?? 0,
        sector: selectedPartner?.sector ?? 0
    });

    // Latch access for the lifetime of the current page. Once granted for a path,
    // the user stays granted until they navigate to a different path. This lets a
    // SysAdmin who was routed in after selecting a partner stay on the page and
    // keep making API requests, even if their partner context briefly changes
    // while they work. The deny test override is authoritative and never latched.
    const grantedPathRef = useRef<string | null>(null);

    if (grantedPathRef.current !== pathname && grantedPathRef.current !== null && !computedAccess) {

        grantedPathRef.current = null;

    }

    if (computedAccess) grantedPathRef.current = pathname;

    const hasAccess = accessTestOverride === 'deny'
        ? false
        : computedAccess || grantedPathRef.current === pathname;

    useEffect(() => {

        if (!hasAccess) {

            onErrorToast(accessTestOverride
                ? `Access denied (test mode: ${accessTestOverride}).`
                : formatMessage({ id: 'you_do_not_have_permission_to_view_that.ef9ee36f', defaultMessage: 'You do not have permission to view that page.' }));

        }

    }, [accessTestOverride, hasAccess, onErrorToast]);

    return (
        <>
            {hasAccess ? children : <Navigate replace to={'/'} />}
        </>
    );

};
